// Cliente opcional para Supabase
// Requiere instalar la dependencia: npm install @supabase/supabase-js

import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const key = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabase = null;
if (url && key) {
  supabase = createClient(url, key);
}

export const isSupabaseConfigured = () => !!supabase;
export { supabase };

// Auth helpers
export const signInWithEmail = async (email, password) => {
  if (!supabase) throw new Error('Supabase no configurado');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const signUpWithEmail = async (email, password) => {
  if (!supabase) throw new Error('Supabase no configurado');
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  if (!supabase) return null;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  return true;
};

export const getUser = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data?.user || null;
};

export const onAuthStateChange = (cb) => {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    cb(event, session);
  });
  return () => data?.subscription?.unsubscribe?.();
};

// Admins table helper: intenta insertar un admin por uid
export const addAdminRemote = async (adminObj) => {
  if (!supabase) throw new Error('Supabase no configurado');
  const { data, error } = await supabase.from('admins').insert([adminObj]).select();
  if (error) throw error;
  return data?.[0] || null;
};

export const fetchAdmins = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('admins').select('*').order('id', { ascending: true });
  if (error) throw error;
  return data;
};

export const deleteAdminRemote = async (uid) => {
  if (!supabase) return null;
  const { error } = await supabase.from('admins').delete().eq('id', uid);
  if (error) throw error;
  return true;
};

export const isUserAdmin = async (uid) => {
  if (!supabase || !uid) return false;
  const { data, error } = await supabase.from('admins').select('id').eq('id', uid).limit(1);
  if (error) throw error;
  return Array.isArray(data) && data.length > 0;
};

// Products
export const fetchProducts = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('products').select('*').order('id', { ascending: true });
  if (error) throw error;
  return data;
};

export const addProductRemote = async (product) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('products').insert([product]).select();
  if (error) throw error;
  return data[0];
};

export const updateProductRemote = async (productId, product) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('products').update(product).eq('id', productId).select();
  if (error) throw error;
  return data[0];
};

export const deleteProductRemote = async (productId) => {
  if (!supabase) return null;
  const { error } = await supabase.from('products').delete().eq('id', productId);
  if (error) throw error;
  return true;
};

// Storage: subir imagen de producto a bucket 'product-images'
export const uploadProductImage = async (file, fileName) => {
  if (!supabase) return null;
  const bucket = 'product-images';
  // If a dedicated upload server is configured, use it (it should use the service_role key)
  const uploadServer = process.env.REACT_APP_UPLOAD_SERVER_URL;
  if (uploadServer) {
    try {
      const fd = new FormData();
      fd.append('file', file, fileName);
      const resp = await fetch(`${uploadServer.replace(/\/$/, '')}/upload`, {
        method: 'POST',
        body: fd
      });
      const json = await resp.json();
      if (!resp.ok) {
        const msg = json?.error || json?.message || resp.statusText;
        throw new Error(`Upload server error: ${msg}`);
      }
      if (!json?.publicUrl) throw new Error('Upload server did not return publicUrl');
      return json.publicUrl;
    } catch (e) {
      // If upload server fails, continue to try direct storage (so developer can see both errors)
      console.warn('Upload via server failed, falling back to direct storage:', e.message || e);
    }
  }
  // NOTE: creating buckets from the client with the anon key is not allowed.
  // Ensure the bucket 'product-images' exists (create it from Supabase UI) and is public.
  const path = `${Date.now()}_${fileName}`;
  // Check bucket exists and is accessible (gives clearer error than generic 400)
  try {
    const { data: bucketInfo, error: bucketError } = await supabase.storage.getBucket(bucket);
    if (bucketError) {
      // if bucket not found or inaccessible, throw a helpful error
      const err = new Error(`Bucket "${bucket}" not found or inaccessible: ${bucketError.message || bucketError}`);
      err.code = 'BUCKET_NOT_FOUND';
      throw err;
    }
  } catch (e) {
    // Some versions of the client might throw; normalize
    if (e && e.message && e.message.includes('404')) {
      const err = new Error(`Bucket "${bucket}" not found (404). Crea el bucket desde la consola de Supabase.`);
      err.code = 'BUCKET_NOT_FOUND';
      throw err;
    }
  }

  // Try to upload; capture full error info for debugging
  const { error: uploadError, data: uploadData } = await supabase.storage.from(bucket).upload(path, file, { cacheControl: '3600', upsert: false });
  if (uploadError) {
    // Build a clearer error with status/message/details when available
    const msg = uploadError.message || JSON.stringify(uploadError);
    const err = new Error(`Storage upload failed: ${msg}`);
    err.status = uploadError.status || null;
    err.details = uploadError.details || null;
    throw err;
  }

  // Get public URL (handle different return shapes across supabase-js versions)
  const publicRes = await supabase.storage.from(bucket).getPublicUrl(path);
  // publicRes may be { data: { publicUrl }, error } or { publicURL, error }
  const publicURL = (publicRes?.data && (publicRes.data.publicUrl || publicRes.data.publicURL)) || publicRes?.publicURL || publicRes?.publicUrl;
  const urlError = publicRes?.error || (!publicURL && new Error('No public URL returned'));
  if (urlError) {
    const err = new Error(`Could not get public URL for uploaded file: ${urlError.message || JSON.stringify(urlError)}`);
    throw err;
  }
  return publicURL;
};

// Categories
export const fetchCategories = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('categories').select('*').order('name', { ascending: true });
  if (error) throw error;
  return data;
};

export const addCategoryRemote = async (category) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('categories').insert([category]).select();
  if (error) throw error;
  return data[0];
};

export const updateCategoryRemote = async (oldName, updatedCategory) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('categories').update(updatedCategory).eq('name', oldName).select();
  if (error) throw error;
  return data[0];
};

export const deleteCategoryRemote = async (categoryName) => {
  if (!supabase) return null;
  const { error } = await supabase.from('categories').delete().eq('name', categoryName);
  if (error) throw error;
  return true;
};

// Customization: types and ingredients
export const fetchCustomTypes = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_types').select('*').order('name', { ascending: true });
  if (error) throw error;
  return data;
};

export const addCustomTypeRemote = async (type) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_types').insert([type]).select();
  if (error) throw error;
  return data?.[0] || null;
};

export const updateCustomTypeRemote = async (id, updated) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_types').update(updated).eq('id', id).select();
  if (error) throw error;
  return data?.[0] || null;
};

export const deleteCustomTypeRemote = async (id) => {
  if (!supabase) return null;
  const { error } = await supabase.from('custom_types').delete().eq('id', id);
  if (error) throw error;
  return true;
};

export const fetchIngredients = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_ingredients').select('*').order('name', { ascending: true });
  if (error) throw error;
  return data;
};

export const addIngredientRemote = async (ingredient) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_ingredients').insert([ingredient]).select();
  if (error) throw error;
  return data?.[0] || null;
};

export const updateIngredientRemote = async (id, updated) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('custom_ingredients').update(updated).eq('id', id).select();
  if (error) throw error;
  return data?.[0] || null;
};

export const deleteIngredientRemote = async (id) => {
  if (!supabase) return null;
  const { error } = await supabase.from('custom_ingredients').delete().eq('id', id);
  if (error) throw error;
  return true;
};

// Orders
export const fetchOrders = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('orders').select('*').order('id', { ascending: true });
  if (error) throw error;
  return data;
};

export const updateOrderStatusRemote = async (orderId, newStatus) => {
  if (!supabase) return null;
  const { data, error } = await supabase.from('orders').update({ status: newStatus }).eq('id', orderId).select();
  if (error) throw error;
  return data[0];
};
